// src/pages/Service/Service Entry/components/ServiceTable.jsx
import React, { useState } from "react";
import { Table, Button, Input, Select } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import InputNumberField from "../../../../../components/Input/InputNumber";
import SearchListAutoComplete from "../../../../../components/SearchListAutoComplete";
import SharedFooter from "../../../../../components/DataTableFooter/SharedFooter";
import {
  addOrderRow,
  deleteOrderRow,
  updateOrderRow,
  setFooterValues,
  setPayServiceCart,
  calculateTotals,
} from "../serviceSlice";
import { selectItems } from "../../../../../store/commonSelectors/lookupsSelectors";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import { getCurrencySymbol } from "../../../../../lib/currency";
import ProductQuickDrawer from "../../../../Product_manage/components/drawers/ProductQuickDrawer";
import { fetchItems } from "../../../../../store/commonThunks/lookups";
import Big from "big.js";
import { getItemDisplayName } from "../../../../../utils";

const ServiceTable = () => {
  const dispatch = useDispatch();
  const items = useSelector(selectItems);
  const footerValues = useSelector((s) => s.serviceEntry.footerValues);
  const itemCart = useSelector((s) => s.serviceEntry.itemCart);
  const taxMode = useSelector((s) => s.serviceEntry.taxMode);
  const conversionRate = useSelector((s) => s.serviceEntry.conversionRate);
  const authInfo = useSelector(selectAuthInfo);
  const currency = authInfo?.currency || "AED";
  const symbol = getCurrencySymbol(currency);

  const isIndia = authInfo?.country?.toLowerCase() === "india";
const taxLabel = isIndia ? "GST" : "VAT";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;
  const [productDrawer, setProductDrawer] = useState({open: false,rowKey: null,});

  const handleUpdate = (rowKey, field, value) => {
    dispatch(updateOrderRow({ rowKey, updates: { [field]: value } }));
    dispatch(calculateTotals({ authInfo }));
  };

  const handleAddRow = () => {
    const newRow = {
      key: Date.now(),
      item_id: null,
      product: "",
      quantity: 1,
      rate: 0,
      tax_per: 0,
      discount: 0,
      discountAmt: null,
      discountMode: "percent",
      item_description: "",
      showDescription: false,
      bonus: false,
    };
    dispatch(addOrderRow(newRow));
  };

  // const handleFooterChange = (value, field) => {
  //   let updatedFooter = { ...footerValues };

  //   if (field === "paidAmount") {
  //     updatedFooter.paidAmount = Number(value) || 0;
  //     updatedFooter.dueAmount = (
  //       parseFloat(updatedFooter.grandTotal || 0) - updatedFooter.paidAmount
  //     ).toFixed(2);
  //   } else {
  //     updatedFooter[field] = value;
  //   }

  //   dispatch(setFooterValues(updatedFooter));
  // };

  const handleFooterChange = (value, field) => {
    let updatedFooter = { ...footerValues };
    if (field === "payCart") {
      const newPaidAmount = value.reduce(
        (sum, item) => sum + parseFloat(item.tran_amount || 0),
        0,
      );
  
      updatedFooter = {
        ...updatedFooter,
        paidAmount: newPaidAmount,
        dueAmount: (
          parseFloat(updatedFooter.grandTotal || 0) - newPaidAmount
        ).toFixed(2),
      };

      // ✅ normalize structure before saving
      const normalizedCart = value.map((v) => ({
        from_acc_id: v.to_acc_id || 0,
        from_acc_name: v.to_acc_name || "",
        tran_amount: String(v.tran_amount || 0),
      }));

      dispatch(setPayServiceCart(normalizedCart));
    } else if (field === "paidAmount") {
      updatedFooter.paidAmount = Number(value) || 0;
      updatedFooter.dueAmount = (
        parseFloat(updatedFooter.grandTotal || 0) - updatedFooter.paidAmount
      ).toFixed(2);
    } else {
      updatedFooter[field] = value;
    }

    dispatch(setFooterValues(updatedFooter));
    dispatch(calculateTotals({ authInfo }));
  };

  const getFilteredServiceItems = (currentRow) => {
    return items.filter((it) => {
      if (it.is_service !== "yes") return false;

      return !itemCart.some((row) => {
        if (row.key === currentRow.key) return false;
        return row.item_id === it.item_id;
      });
    });
  };

  const baseColumns = [
    // {
    //   title: "Bonus",
    //   dataIndex: "bonus",
    //   render: (_, record) => (
    //     <Checkbox
    //       checked={record.bonus}
    //       onChange={(e) => handleUpdate(record.key, "bonus", e.target.checked)}
    //     />
    //   ),
    // },
    {
      title: "Service Item",
      dataIndex: "product",
      render: (_, record) => (
        <div>
        <SearchListAutoComplete
          entityType="item"
          placeholder="Service Item"
          showAddNew={true}
          style={{ width: "100%" }}
          optionsList={getFilteredServiceItems(record).map((i) => ({
            sale_id: i.item_id,
            display_text: getItemDisplayName(i),
            raw: i,
          }))}
          value={
            record.item_id
              ? {
                  // `product` is also what gets saved as this row's
                  // item_name, so it stays the plain name -- the fuller
                  // "name - code - barcode" label only lives in
                  // product_display, used for showing/picking in this field.
                  value: record.product_display || record.product,
                  label: record.product_display || record.product,
                  display_text: record.product_display || record.product,
                  raw: record,
                }
              : null
          }
          onSelect={(obj) => {

            if (obj === "add_new") {
              setProductDrawer({
                open: true,
                rowKey: record.key,
              });
              return;
            }

            const it =
              items.find((i) => i.item_id === obj?.sale_id) || obj?.raw;
            handleUpdate(record.key, "item_id", it.item_id);
            handleUpdate(record.key, "product", it.item_name);
            handleUpdate(record.key, "product_display", getItemDisplayName(it));
            handleUpdate(record.key, "rate", it.sale_rate ?? 0);
            handleUpdate(record.key, "tax_per", it.tax_per ?? 0);
            handleUpdate(record.key, "item_code", it.item_code ?? 0);
            handleUpdate(record.key, "unit_id", it.unit_id || 0);
            handleUpdate(record.key, "unit_name", it.unit_name || "");
            handleUpdate(record.key, "unit_symbol", it.unit_symbol || "");
            handleUpdate(record.key, "per_unit_id", it.unit_id || 0);
            handleUpdate(record.key, "per_unit_symbol", it.unit_symbol || "");
            handleUpdate(record.key, "per_conversion", it.conversion || 1);
            handleUpdate(record.key, "warehouse_id", it.warehouse_id || 0);
            handleUpdate(record.key, "warehouse_name", it.warehouse_name || "");
            handleUpdate(record.key, "base_unit_name", it.base_unit_name || "");
            handleUpdate(record.key, "conversion", it.conversion || 1);
            handleUpdate(record.key, "units", it.units || []);
            // Pre-fill the line description from the product's Narration / Description
            // (editable afterwards). Replaces the previous product's text.
            const narration = String(it.narration ?? "").trim();
            handleUpdate(record.key, "item_description", narration);
            handleUpdate(record.key, "showDescription", !!narration);
          }}
        />
        {record.item_id && (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() =>
            handleUpdate(
              record.key,
              "showDescription",
              !record.showDescription
            )
          }
        >
          {record.showDescription
            ? "Hide Description"
            : "Add Description"}
        </Button>
      )}

      {record.showDescription && (
        <Input.TextArea
          rows={2}
          placeholder="Add item description"
          value={record.item_description}
          onChange={(e) =>
            handleUpdate(
              record.key,
              "item_description",
              e.target.value
            )
          }
        />
      )}
    </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      render: (_, record) => (
        <InputNumberField
          min={0}
          max={100000}
          value={record.quantity}
          placeholder="1"
          onClick={() => {
            if (record.quantity !== null) {
              handleUpdate(record.key, "quantity", null);
            }
          }}
          onChange={(v) => handleUpdate(record.key, "quantity", v)}
          precision={1}
          step={0.5}
          formatter={(value) => {
            if (!value && value !== 0) return "";
            return Math.floor(Number(value)).toString();
          }}
          parser={(value) => {
            if (!value) return 0;
            return parseInt(value.replace(/[^\d]/g, "")) || 0;
          }}
          onKeyPress={(e) => {
            if ([".", ","].includes(e.key)) {
              e.preventDefault();
            }
          }}
          onPaste={(e) => {
            // Prevent pasting decimal values
            const pasteData = e.clipboardData.getData("text");
            if (pasteData.includes(".") || pasteData.includes(",")) {
              e.preventDefault();
            }
          }
        }
        />
      ),
    },
    {
      title: "Rate",
      dataIndex: "rate",

      render: (_, record) => (
        <InputNumberField
          min={0}
          disabled
          precision={2}
          value={parseFloat(
            conversionRate ? record?.rate * conversionRate : record?.rate,
          )?.toFixed(2)}
          onChange={(v) => handleUpdate(record.key, "rate", v)}
        />
      ),
    },
  ];

  const individualColumns = [
    {
      title: "Discount",
      dataIndex: "discount",
      width: 160,
      render: (_, record) => {
        const mode = record.discountMode === "amount" ? "amount" : "percent";
        const lineTotal = new Big(Number(record.quantity) || 0).times(Number(record.rate) || 0);
        // One merged compound field (number input + unit-select addon)
        // instead of a separate Select stacked above the number input.
        const modeSelect = (
          <Select
            size="small"
            style={{ width: 65 }}
            value={mode}
            options={[
              { label: "%", value: "percent" },
              { label: symbol, value: "amount" },
            ]}
            onChange={(v) => handleUpdate(record.key, "discountMode", v)}
          />
        );
        return mode === "amount" ? (
          <InputNumberField
            min={0}
            max={Number(lineTotal)}
            precision={2}
            value={record.discountAmt}
            onChange={(v) => handleUpdate(record.key, "discountAmt", v)}
            addonAfter={modeSelect}
          />
        ) : (
          <InputNumberField
            min={0}
            max={100}
            precision={2}
            value={record.discount}
            placeholder="0"
            onClick={() => {
              if (record.discount !== null) {
                handleUpdate(record.key, "discount", null);
              }
            }}
            onChange={(v) => handleUpdate(record.key, "discount", v)}
            addonAfter={modeSelect}
          />
        );
      },
    },
  ];

  const gstColumn = showTax
    ? [
      {
       title: `${taxLabel} %`,
        dataIndex: "tax_per",
        render: (_, record) => (
          <InputNumberField
            min={0}
            max={100}
            precision={2}
            value={record.tax_per}
             placeholder="0"
            onClick={() => {
              if (record.tax_per !== null) {
                handleUpdate(record.key, "tax_per", null);
              }
            }}
            onChange={(v) => handleUpdate(record.key, "tax_per", v)}
            // disabled
          />
        ),
      },
    ]
    : [];

  const amountColumn = [
    {
      title: "Line Total",
      render: (_, r) => {
        if (r.bonus) return "0.00";

        const qty = Big(r.quantity || 0);
        const rate = Big(r.rate || 0);
        let line = qty.times(rate);

        if (taxMode === "on_item") {
          const discountMode = r.discountMode === "amount" ? "amount" : "percent";
          let discountAmt;
          if (discountMode === "amount") {
            const raw = Big(r.discountAmt || 0);
            discountAmt = raw.gt(line) ? line : raw.lt(0) ? Big(0) : raw;
          } else {
            discountAmt = line.times(Big(r.discount || 0)).div(100);
          }
          line = line.minus(discountAmt);
          const taxAmt = showTax ? line.times(Big(r.tax_per || 0)).div(100) : Big(0);
          line = line.plus(taxAmt);
        }
        const convertedLine =
          conversionRate ? line.times(conversionRate).toFixed(2) : line.toFixed(2);
        return convertedLine;
      },
    },
    {
      title: "",
      render: (_, r) => (
        <Button
          icon={<DeleteOutlined />}
          danger
          onClick={() => dispatch(deleteOrderRow(r.key))}
          disabled={itemCart.length === 1}
        />
      ),
    },
  ];

  const columns =
    taxMode === "on_item"
      ? [...baseColumns, ...individualColumns, ...gstColumn, ...amountColumn]
      : [...baseColumns, ...amountColumn];

  return (
    <div>
      <Button
        type="dashed"
        onClick={handleAddRow}
        icon={<PlusOutlined />}
        style={{ marginBottom: 8 }}
      >
        Add Row
      </Button>
      <div style={{ width: "100%", maxWidth: "1300px" }}>
        <Table
          dataSource={itemCart}
          columns={columns}
          pagination={false}
          rowKey="key"
          scroll={{ x: "min-content" }}
          footer={() => (
            <SharedFooter
              footerValues={footerValues}
              dataSource={itemCart}
              onFooterChange={handleFooterChange}
              pageType="serviceEntry"
              taxMode={taxMode}
              conversionRate={conversionRate}
            />
          )}
        />
      </div>

      <ProductQuickDrawer
       fromServiceEntry={true} 
        open={productDrawer.open}
        onClose={() => setProductDrawer({ open: false, rowKey: null })}
        onCreated={(product) => {
          if (!product) return;

          setProductDrawer({ open: false, rowKey: null });

          dispatch(fetchItems(""));

          const productValue = {
            ...product,
            item_id: product.item_id,
            item_name: product.item_name,
            display_text: product.item_name,
            tax_per: product.tax_per,
            sale_rate: product.sale_rate,
            raw: product,
          };

          const it =
            items.find((i) => i.item_id === productValue?.sale_id) ||
            productValue?.raw;
          handleUpdate(productDrawer.rowKey, "item_id", it.item_id);
          handleUpdate(productDrawer.rowKey, "product", it.item_name);
          handleUpdate(productDrawer.rowKey, "product_display", getItemDisplayName(it));
          handleUpdate(productDrawer.rowKey, "rate", it.sale_rate ?? 0);
          handleUpdate(productDrawer.rowKey, "tax_per", it.tax_per ?? 0);
          handleUpdate(productDrawer.rowKey, "item_code", it.item_code ?? 0);
          handleUpdate(productDrawer.rowKey, "unit_id", it.unit_id || 0);
          handleUpdate(productDrawer.rowKey, "unit_name", it.unit_name || "");
          handleUpdate(
            productDrawer.rowKey,
            "unit_symbol",
            it.unit_symbol || "",
          );
          handleUpdate(productDrawer.rowKey, "per_unit_id", it.unit_id || 0);
          handleUpdate(
            productDrawer.rowKey,
            "per_unit_symbol",
            it.unit_symbol || "",
          );
          handleUpdate(
            productDrawer.rowKey,
            "per_conversion",
            it.conversion || 1,
          );
          handleUpdate(
            productDrawer.rowKey,
            "warehouse_id",
            it.warehouse_id || 0,
          );
          handleUpdate(
            productDrawer.rowKey,
            "warehouse_name",
            it.warehouse_name || "",
          );
          handleUpdate(
            productDrawer.rowKey,
            "base_unit_name",
            it.base_unit_name || "",
          );
          handleUpdate(productDrawer.rowKey, "conversion", it.conversion || 1);
          handleUpdate(productDrawer.rowKey, "units", it.units || []);
          const narration = String(it.narration ?? "").trim();
          handleUpdate(productDrawer.rowKey, "item_description", narration);
          handleUpdate(productDrawer.rowKey, "showDescription", !!narration);
        }}
      />
    </div>
  );
};

export default ServiceTable;
